import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { ArrowRight, ChevronDown, Menu, Search, ShoppingBag, X } from 'lucide-react';
import { useStore } from '@/components/StoreContext';
import { StoreSocialLinks } from '@/components/StoreSocialLinks';
import { hasMinimumSearchLength } from '@/lib/search-utils';
import { getListPublicCatalogBrandsQueryKey, suggestCatalogSearch, useListPublicCatalogBrands } from '@workspace/api-client-react';
import type { SearchSuggestion } from '@workspace/api-client-react';

export function BrandMark({ onClick }: { onClick?: () => void } = {}) {
  const { storefrontSettings } = useStore();
  const logoUrl = storefrontSettings.content.logoUrl;
  return <Link href="/" onClick={onClick} className="flex items-center gap-2.5" aria-label="Chamakpatti home" data-testid="link-brand-home">
    {logoUrl
      ? <img src={logoUrl} alt="ChamakPatti" width={180} height={40} fetchPriority="high" decoding="async" className="h-10 w-[180px] object-contain" data-testid="img-header-logo"/>
      : <span className="block h-10 w-[180px]" aria-hidden="true" data-testid="placeholder-header-logo"/>}
  </Link>;
}
export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [mobileBrandsOpen, setMobileBrandsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [location, navigate] = useLocation();
  const { itemCount, storefrontSettings } = useStore();
  const publicBrands = useListPublicCatalogBrands({ query: { queryKey: getListPublicCatalogBrandsQueryKey() } });
  const brandsDropdownRef = useRef<HTMLDivElement>(null);
  const brandBySlug = new Map<string, { slug: string; name: string; href: string; description: string }>();
  storefrontSettings.brands.forEach(brand => brandBySlug.set(brand.slug, brand));
  (publicBrands.data?.brands ?? []).filter(brand => brand.productCount > 0).forEach(brand => {
    if (!brandBySlug.has(brand.slug)) {
      brandBySlug.set(brand.slug, { slug: brand.slug, name: brand.name, href: `/brands/${brand.slug}`, description: "" });
    }
  });
  const configuredBrandOrder = storefrontSettings.brandOrder ?? [];
  const brands = [
    ...configuredBrandOrder
      .filter(brand => brand.visible)
      .map(brand => brandBySlug.get(brand.slug))
      .filter((brand): brand is { slug: string; name: string; href: string; description: string } => Boolean(brand)),
    ...[...brandBySlug.values()].filter(brand => !configuredBrandOrder.some(entry => entry.slug === brand.slug)),
  ];
  const saleEnabled = storefrontSettings.saleEnabled;
  const currentBrandSlug = location.match(/^\/brands\/([^/]+)/)?.[1];
  function closeMobileMenu() {
    setMenuOpen(false);
    setMobileBrandsOpen(false);
  }
  useEffect(() => {
    closeMobileMenu();
  }, [location]);
  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeMobileMenu();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);
  useEffect(() => {
    if (!brandsOpen) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !brandsDropdownRef.current?.contains(event.target)) setBrandsOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setBrandsOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [brandsOpen]);
  useEffect(() => {
    if (!searchOpen || !hasMinimumSearchLength(query)) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }
    const controller = new AbortController();
    setSuggestions([]);
    setActiveSuggestion(-1);
    setSuggestionsLoading(true);
    const timer = window.setTimeout(() => {
      void suggestCatalogSearch({ q: query.trim(), ...(currentBrandSlug ? { brand: currentBrandSlug } : {}) }, { signal: controller.signal })
        .then(response => setSuggestions(response.suggestions.slice(0, 6)))
        .catch(() => { if (!controller.signal.aborted) setSuggestions([]); })
        .finally(() => { if (!controller.signal.aborted) setSuggestionsLoading(false); });
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [query, searchOpen, currentBrandSlug]);
  function submitSearch(e?: FormEvent) {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    const params = new URLSearchParams({ q });
    const brand = currentBrandSlug;
    if (brand) params.set("brand", brand);
    if (!window.location.pathname.startsWith("/search")) {
      try { sessionStorage.setItem("chamakpatti-search-origin", `${window.location.pathname}${window.location.search}`); } catch { /* storage is optional */ }
    }
    navigate(`/search?${params.toString()}`);
    window.dispatchEvent(new CustomEvent("store-search-url", { detail: { q, brand: brand ?? "" } }));
    setSearchOpen(false);
    setMenuOpen(false);
    setSuggestions([]);
  }
  function closeSearch() {
    setSearchOpen(false);
    setSuggestions([]);
    if (location === "/search" || location.startsWith("/search?")) {
      let origin = "";
      try { origin = sessionStorage.getItem("chamakpatti-search-origin") ?? ""; } catch { /* storage is optional */ }
      navigate(origin && !origin.startsWith("/search") ? origin : "/");
    }
  }
  function chooseSuggestion(suggestion: SearchSuggestion) {
    const q = suggestion.value || suggestion.label;
    const params = new URLSearchParams({ q });
    const brand = suggestion.brandSlug ?? currentBrandSlug;
    if (brand) params.set("brand", brand);
    if (!window.location.pathname.startsWith("/search")) {
      try { sessionStorage.setItem("chamakpatti-search-origin", `${window.location.pathname}${window.location.search}`); } catch { /* storage is optional */ }
    }
    navigate(`/search?${params.toString()}`);
    window.dispatchEvent(new CustomEvent("store-search-url", { detail: { q, brand: brand ?? "" } }));
    setQuery(q);
    setSearchOpen(false);
    setSuggestions([]);
  }
  function onSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" && suggestions.length) {
      event.preventDefault();
      setActiveSuggestion(index => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp" && suggestions.length) {
      event.preventDefault();
      setActiveSuggestion(index => index <= 0 ? suggestions.length - 1 : index - 1);
    } else if (event.key === "Escape") {
      if (suggestions.length) { setSuggestions([]); setActiveSuggestion(-1); }
      else closeSearch();
    } else if (event.key === "Enter" && activeSuggestion >= 0 && suggestions[activeSuggestion]) {
      event.preventDefault();
      chooseSuggestion(suggestions[activeSuggestion]);
    }
  }
  return <>
    <div className="flex h-8 items-center justify-center bg-[#171717] px-4 text-center text-[9px] tracking-[.08em] text-[#f5f2eb] sm:text-[10px] sm:tracking-[.12em]">{storefrontSettings.content.announcementText}</div>
    <header className="sticky top-0 z-40 border-b border-[#d7d3cb] bg-[#f7f5ef]/95 backdrop-blur">
      <div className="mx-auto flex h-[76px] max-w-[1440px] items-center justify-between px-5 md:px-10">
        <button className="min-h-11 min-w-11 p-2 md:hidden" aria-label="Open menu" data-testid="button-open-menu" onClick={() => setMenuOpen(true)}><Menu size={19}/></button>
        <div ref={brandsDropdownRef} className="relative hidden w-[180px] items-center gap-7 text-[11px] tracking-[.1em] md:flex">
          <button type="button" onClick={() => setBrandsOpen(open => !open)} aria-expanded={brandsOpen} aria-haspopup="menu" data-testid="button-brands-menu">BRANDS</button>
          <Link href="/shop" data-testid="link-shop-nav">SHOP</Link>
          {saleEnabled && <Link href="/sale" className="sale-nav-link" data-testid="link-sale-nav">SALE</Link>}
          {brandsOpen && <div role="menu" className="absolute left-0 top-full z-50 mt-5 flex max-h-80 min-w-48 flex-col overflow-y-auto border border-[#d7d3cb] bg-[#f7f5ef] py-2 shadow-lg">
            <Link role="menuitem" href="/brands" onClick={() => setBrandsOpen(false)} className="px-4 py-3 text-[10px] tracking-[.12em]">ALL BRANDS</Link>
            {brands.map(brand => <Link key={brand.slug} role="menuitem" href={brand.href} onClick={() => setBrandsOpen(false)} className="px-4 py-3 text-[10px] tracking-[.12em] hover:bg-[#e7e2d9]" data-testid={`link-shop-${brand.slug}`}>SHOP {brand.name.toUpperCase()}</Link>)}
          </div>}
        </div>
        <BrandMark />
        <div className="flex w-[180px] items-center justify-end gap-4">
          <button onClick={() => setSearchOpen(v => !v)} className="flex min-h-11 min-w-11 items-center justify-center gap-2 p-2 text-[10px] tracking-[.13em]" aria-label="Search" data-testid="button-search"><Search size={17}/><span className="hidden lg:block">SEARCH</span></button>
          <Link href="/cart" className="relative flex min-h-11 min-w-11 items-center justify-center gap-2 p-2 text-[10px] tracking-[.13em]" aria-label="Shopping bag" data-testid="link-cart"><ShoppingBag size={17}/><span className="hidden lg:block">BAG</span><span className="mono absolute -right-1 -top-1 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[#171717] px-1 text-[9px] text-white">{itemCount}</span></Link>
        </div>
      </div>
      {searchOpen && <form onSubmit={submitSearch} className="mx-auto max-w-[1440px] border-t border-[#ddd9d1] px-5 py-3 md:px-10" role="search">
        <div className="flex items-center gap-3"><Search size={17} className="shrink-0"/><input autoFocus value={query} maxLength={120} onChange={e => { setQuery(e.target.value); setSuggestions([]); setActiveSuggestion(-1); }} onKeyDown={onSearchKeyDown} className="field border-0 border-b px-0 py-2 text-sm" placeholder="Search the collection" aria-label="Search products, brands, and categories" role="combobox" aria-autocomplete="list" aria-expanded={suggestions.length > 0 || suggestionsLoading} aria-controls="store-search-suggestions" aria-activedescendant={activeSuggestion >= 0 ? `search-suggestion-${activeSuggestion}` : undefined} data-testid="input-store-search"/><button className="mono text-xs" data-testid="button-submit-search">SEARCH</button><button type="button" onClick={closeSearch} aria-label="Close search" className="p-1" data-testid="button-close-search"><X size={17}/></button></div>
        {(suggestions.length > 0 || suggestionsLoading) && <div id="store-search-suggestions" role="listbox" aria-label="Search suggestions" className="ml-8 mt-2 border border-[#ddd9d1] bg-[#f7f5ef] py-1 shadow-sm" data-testid="list-search-suggestions">
          {suggestionsLoading && !suggestions.length && <p className="px-4 py-3 text-xs text-[#5f5b55]" role="status">Looking across the collection…</p>}
          {suggestions.slice(0, 6).map((suggestion, index) => <button key={`${suggestion.type}-${suggestion.value}-${index}`} id={`search-suggestion-${index}`} type="button" role="option" aria-selected={index === activeSuggestion} onMouseEnter={() => setActiveSuggestion(index)} onClick={() => chooseSuggestion(suggestion)} className={`flex w-full items-center justify-between gap-4 px-4 py-3 text-left text-sm ${index === activeSuggestion ? "bg-[#e7e2d9]" : "hover:bg-[#eee9df]"}`} data-testid={`option-search-suggestion-${index}`}><span>{suggestion.label}</span><span className="mono shrink-0 text-[8px] tracking-[.14em] text-[#815348]">{suggestion.type.toUpperCase()}</span></button>)}
        </div>}
      </form>}
    </header>
    {menuOpen && <div className="mobile-menu fixed inset-0 z-50 flex flex-col bg-[#f7f5ef] px-6 pb-[max(16px,env(safe-area-inset-bottom))] pt-4 md:hidden" role="dialog" aria-modal="true" aria-label="Main menu" data-testid="mobile-menu">
      <div className="flex shrink-0 items-center justify-between border-b border-[#d7d3cb] pb-3">
        <BrandMark onClick={closeMobileMenu}/>
        <button type="button" className="inline-flex min-h-11 min-w-11 items-center justify-center" aria-label="Close menu" onClick={closeMobileMenu} data-testid="button-close-menu"><X aria-hidden="true"/></button>
      </div>
      <nav className="mobile-menu__nav mt-2 flex min-h-0 flex-1 flex-col overflow-y-auto" aria-label="Main navigation">
        <Link href="/shop" className="mobile-menu__item" onClick={closeMobileMenu} data-testid="link-mobile-shop">Shop all</Link>
        <div>
          <button type="button" className="mobile-menu__item w-full" aria-expanded={mobileBrandsOpen} aria-controls="mobile-brands-list" onClick={() => setMobileBrandsOpen(open => !open)} data-testid="button-mobile-brands">
            <span>Brands</span><ChevronDown size={17} className={`shrink-0 transition-transform ${mobileBrandsOpen ? "rotate-180" : ""}`} aria-hidden="true"/>
          </button>
          {mobileBrandsOpen && <div id="mobile-brands-list" className="mobile-menu__brand-list" role="region" aria-label="Shop by brand" data-testid="mobile-brand-list">
            <Link href="/brands" className="mobile-menu__brand-item storefront-info-text" onClick={closeMobileMenu} data-testid="link-mobile-all-brands">All brands</Link>
            {brands.map(brand => <Link key={brand.slug} href={brand.href} className="mobile-menu__brand-item storefront-info-text" onClick={closeMobileMenu} data-testid={`link-mobile-shop-${brand.slug}`}>Shop {brand.name}</Link>)}
          </div>}
        </div>
        {saleEnabled && <Link href="/sale" className="mobile-menu__item sale-nav-link" onClick={closeMobileMenu} data-testid="link-mobile-sale">Sale</Link>}
        <button type="button" className="mobile-menu__item w-full" onClick={() => { closeMobileMenu(); setSearchOpen(true); }} data-testid="button-mobile-search"><span>Search</span><Search size={17} aria-hidden="true"/></button>
        <Link href="/cart" className="mobile-menu__item" onClick={closeMobileMenu} data-testid="link-mobile-bag"><span>Your bag</span><span className="mono text-sm">({itemCount})</span></Link>
      </nav>
      <div className="mobile-menu__footer shrink-0 border-t border-[#d7d3cb] pt-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav className="flex flex-wrap items-center gap-x-4" aria-label="Footer links">
            <Link href="/our-story" className="mobile-menu__footer-link storefront-info-text" onClick={closeMobileMenu}>Our Story</Link>
            <Link href="/terms" className="mobile-menu__footer-link storefront-info-text" onClick={closeMobileMenu}>Terms of service</Link>
          </nav>
          <StoreSocialLinks tone="light"/>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs tracking-[.1em]"><span>A RIOT OF COLOR</span><ArrowRight size={14} aria-hidden="true"/></div>
      </div>
    </div>}
  </>;
}
export function Footer() {
  const { storefrontSettings } = useStore();
  const publicBrands = useListPublicCatalogBrands({ query: { queryKey: getListPublicCatalogBrandsQueryKey() } });
  const knownSlugs = new Set(storefrontSettings.brands.map(brand => brand.slug));
  const brands = [...storefrontSettings.brands, ...(publicBrands.data?.brands ?? []).filter(brand => !knownSlugs.has(brand.slug) && brand.productCount > 0).map(brand => ({ slug: brand.slug, name: brand.name, href: `/brands/${brand.slug}`, description: "" }))];
  const content = storefrontSettings.content;
  const footerLogoUrl = content.footerLogoUrl || `${import.meta.env.BASE_URL}footer-logo-white.png`;
  return <footer className="mt-24 bg-[#171717] text-[#f7f5ef]">
    <div className="mx-auto grid max-w-[1440px] gap-12 px-6 py-14 md:grid-cols-[1.3fr_.7fr_.7fr] md:px-10 md:py-20">
      <div><img src={footerLogoUrl} alt="ChamakPatti" width={180} height={40} loading="lazy" decoding="async" className="h-10 w-[180px] object-contain" data-testid="img-footer-logo"/><p className="serif mt-8 max-w-sm whitespace-pre-line text-3xl leading-tight">{content.footerHeading}</p><p className="mt-4 max-w-sm whitespace-pre-line text-xs leading-6 text-white/60">{content.footerBody}</p></div>
       <div><p className="mono mb-5 text-[10px] tracking-[.2em] text-white/50">EXPLORE</p><div className="flex flex-col gap-3 text-sm"><Link href="/our-story">Our Story</Link><Link href="/shop">Shop all</Link><Link href="/brands">Brands</Link>{brands.map(brand => <Link key={brand.slug} href={brand.href}>Shop {brand.name}</Link>)}<Link href="/terms">Terms of service</Link></div></div>
       <div><p className="mono mb-5 text-[10px] tracking-[.2em] text-white/50">THE PROMISE</p><p className="whitespace-pre-line text-sm leading-7">{content.footerPromiseText}</p><div className="mt-7 border-t border-white/20 pt-5"><p className="mono mb-3 text-[9px] tracking-[.18em] text-white/50">FOLLOW CHAMAKPATTI</p><StoreSocialLinks/></div></div>
    </div><div className="mx-auto flex max-w-[1440px] flex-col gap-2 border-t border-white/20 px-6 py-5 text-[10px] tracking-wider text-white/55 md:flex-row md:justify-between md:px-10"><span>© {new Date().getFullYear()} {content.footerCopyrightLabel}</span><span>{content.footerTagline}</span></div>
  </footer>;
}